/**
 * Width ceiling for hand-drawn `viewBox` SVG charts.
 *
 * The bug this exists to prevent
 * ─────────────────────────────
 * An `<svg viewBox="0 0 W H" class="w-full">` with no height constraint scales
 * UNIFORMLY: the browser preserves the viewBox aspect ratio, so the rendered
 * height and every `fontSize` inside grow in lockstep with the width. That is
 * invisible at normal pane sizes and grotesque past them. Measured on the
 * Stock Details pane maximized on a 49" ultrawide:
 *
 *     QuarterlyBars design size   272 x 142 px
 *     rendered in a ~5000px pane  = 18.4x  ->  5000 x 2611 px
 *     its fontSize="7" axis labels          ->  ~129 px tall
 *
 * That is the "98% / 214%" wall of text in the bug report — not a styling
 * quirk but a chart rendered eighteen times its design size.
 *
 * The other failure mode is `preserveAspectRatio="none"`: the plot fills the
 * pane, but type and stroke stretch with it — a 900×420 payoff on a 1800×200
 * Trade pane becomes a pancake, and browser zoom makes it worse. Fill charts
 * (payoff, risk, greeks) layout at the pane's real pixel size via
 * `usePaneBox` instead.
 *
 * Why a cap rather than a responsive redraw (for SMALL charts)
 * ─────────────────────────────────────────
 * The honest fix for "fill the width without scaling the type" is to measure
 * the container and recompute the layout at real pixel width (a ResizeObserver
 * per chart, and a re-think of tick density for every one of them). These are
 * small static charts where that is not worth it; what they need is to stop
 * growing. So the cap lets them grow to a comfortable multiple of the size
 * they were drawn at, then stop and sit left-aligned in the extra space.
 *
 * `2x` is deliberately generous — a chart at twice its design size is still
 * crisp and readable on a big monitor, and the failure it guards against is an
 * order of magnitude past that, so tuning the exact factor buys nothing.
 */
export const SVG_SCALE_CAP = 2;

/**
 * Style for a `w-full` viewBox SVG so it can't scale past `SVG_SCALE_CAP`x its
 * design width. Pass the SAME `W` used in the `viewBox`.
 *
 *     <svg viewBox={`0 0 ${W} ${H}`} className="w-full" style={svgWidthCap(W)}>
 */
export function svgWidthCap(designWidth: number): { maxWidth: number } {
  return { maxWidth: designWidth * SVG_SCALE_CAP };
}
